import { useEffect, useMemo } from "react"
import { useAuth } from "../auth"
import { startRealtime } from "../realtime/socket"
import {
	allProjects,
	canWithdraw,
	claim,
	loadMe,
	openThread,
	refreshMetrics,
	route,
	toggleGithub,
	toggleLeft,
	toggleRight,
} from "../store/actions"
import { isOpenRequest } from "../store/derived"
import { getState, setUi, useStore } from "../store/store"
import { visibleRoots } from "../store/visible"
import { CommandPalette } from "./CommandPalette"
import { ConnectPanel } from "./ConnectPanel"
import { Center } from "./Conversation"
import { ResolveDialog, ShortcutSheet, WithdrawDialog } from "./Dialogs"
import { LeftRail } from "./LeftRail"
import { FirstProject, NewProjectDialog } from "./NewProjectDialog"
import { RightPanel } from "./RightPanel"
import { SettingsDrawer } from "./SettingsDrawer"
import { ProjectsHome } from "./ProjectsHome"
import { TopBar } from "./TopBar"

export function Dashboard() {
	const auth = useAuth()
	const me = useStore(s => s.me)
	const meError = useStore(s => s.meError)
	const ui = useStore(s => s.ui)
	const projectIds = useMemo(
		() =>
			me
				? allProjects()
						.map(p => p.id)
						.sort()
				: [],
		[me],
	)
	const key = projectIds.join(",")

	useEffect(() => {
		void loadMe()
		// Back/forward between the projects page and a project.
		const onPop = () => void route()
		addEventListener("popstate", onPop)
		return () => removeEventListener("popstate", onPop)
	}, [])
	const projectId = useStore(s => s.projectId)

	useEffect(() => {
		if (!key) return
		return startRealtime(auth.getToken, key.split(","))
	}, [key, auth.getToken])

	// The counters (p50/p95, per-agent stats) are the only thing refreshed on a timer.
	useEffect(() => {
		const t = setInterval(
			() => document.visibilityState === "visible" && void refreshMetrics(),
			15_000,
		)
		return () => clearInterval(t)
	}, [])

	useKeyboard()

	if (meError && !me)
		return (
			<div className="page">
				<div className="card">
					<div className="card-h">Could not reach Walkie</div>
					<div className="dim">{meError}</div>
					<button className="btn" onClick={() => void loadMe()}>
						Retry
					</button>
				</div>
			</div>
		)

	const noProjects = !!me && projectIds.length === 0
	// The projects page (and the first-run screen) take the whole width.
	const home = !!me && !projectId
	const cls = [
		"shell",
		(!ui.leftOpen || home) && "no-left",
		(!ui.rightOpen || home) && "no-right",
	]
		.filter(Boolean)
		.join(" ")

	return (
		<div className={cls}>
			<TopBar />
			<LeftRail />
			{!me ? (
				<main className="center">
					<div className="loading-bar" />
				</main>
			) : noProjects ? (
				<main className="center">
					<FirstProject />
				</main>
			) : home ? (
				<ProjectsHome />
			) : (
				<Center />
			)}
			{!home && <RightPanel />}
			{ui.palette && <CommandPalette />}
			{ui.shortcuts && <ShortcutSheet />}
			{ui.connect && <ConnectPanel />}
			{ui.settings && <SettingsDrawer />}
			{ui.newProject && <NewProjectDialog />}
			{ui.resolveFor && <ResolveDialog mid={ui.resolveFor} />}
			{ui.withdrawFor && <WithdrawDialog mid={ui.withdrawFor} />}
			<Toasts />
		</div>
	)
}

function Toasts() {
	const toasts = useStore(s => s.toasts)
	return (
		<div className="toasts" role="status" aria-live="polite">
			{toasts.map(t => (
				<div key={t.id} className={"toast " + t.kind}>
					{t.text}
				</div>
			))}
		</div>
	)
}

const typing = (t: EventTarget | null) => {
	const el = t as HTMLElement | null
	if (!el) return false
	return (
		el.tagName === "INPUT" ||
		el.tagName === "TEXTAREA" ||
		el.tagName === "SELECT" ||
		el.isContentEditable
	)
}

function useKeyboard() {
	useEffect(() => {
		let gAt = 0
		const onKey = (e: KeyboardEvent) => {
			const s = getState()
			const ui = s.ui
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
				e.preventDefault()
				setUi({ palette: !ui.palette })
				return
			}
			if (e.defaultPrevented || typing(e.target) || e.metaKey || e.ctrlKey || e.altKey)
				return
			if (
				ui.palette ||
				ui.shortcuts ||
				ui.resolveFor ||
				ui.withdrawFor ||
				ui.connect ||
				ui.settings ||
				ui.newProject
			)
				return

			if (Date.now() - gAt < 1200) {
				gAt = 0
				if (e.key === "r") {
					e.preventDefault()
					if (!ui.leftOpen) toggleLeft()
					requestAnimationFrame(() =>
						(
							document.querySelector<HTMLElement>('#rail-rooms [aria-current="true"]') ??
							document.querySelector<HTMLElement>("#rail-rooms button")
						)?.focus(),
					)
				} else if (e.key === "h") {
					e.preventDefault()
					toggleGithub()
				} else if (e.key === "a") {
					e.preventDefault()
					if (!ui.rightOpen) toggleRight()
					requestAnimationFrame(() =>
						document.querySelector<HTMLElement>(".agent")?.focus(),
					)
				}
				return
			}

			const sel = ui.selectedId ? s.messages[ui.selectedId] : undefined
			switch (e.key) {
				case "g":
					gAt = Date.now()
					return
				case "j":
				case "k":
				case "ArrowDown":
				case "ArrowUp": {
					if (
						(e.key === "ArrowDown" || e.key === "ArrowUp") &&
						!(e.target as HTMLElement)?.closest?.(".list")
					)
						return
					const list = visibleRoots(s)
					if (!list.length) return
					e.preventDefault()
					const i = list.findIndex(m => m.id === ui.selectedId)
					const down = e.key === "j" || e.key === "ArrowDown"
					const next =
						i < 0
							? list.length - 1
							: Math.max(0, Math.min(list.length - 1, i + (down ? 1 : -1)))
					setUi({ selectedId: list[next]!.id })
					return
				}
				case "Enter":
					if (
						sel &&
						!typing(e.target) &&
						(e.target as HTMLElement).tagName !== "BUTTON"
					) {
						e.preventDefault()
						void openThread(sel.id)
					}
					return
				case "r":
					e.preventDefault()
					if (sel) void openThread(sel.id, true)
					else setUi({ focusComposer: { target: "main", tick: Date.now() } })
					return
				case "n":
					e.preventDefault()
					setUi({ focusComposer: { target: "main", tick: Date.now() } })
					return
				case "c":
					if (sel?.request?.status === "open" && s.role !== "viewer") {
						e.preventDefault()
						void claim(sel.id)
					}
					return
				case "e":
					if (sel && isOpenRequest(sel) && s.role !== "viewer") {
						e.preventDefault()
						setUi({ resolveFor: sel.id })
					}
					return
				case "w":
					if (sel && canWithdraw(sel, s)) {
						e.preventDefault()
						setUi({ withdrawFor: sel.id })
					}
					return
				case "/":
					e.preventDefault()
					setUi({ palette: true })
					return
				case "?":
					e.preventDefault()
					setUi({ shortcuts: true })
					return
				case "[":
					e.preventDefault()
					toggleLeft()
					return
				case "]":
					e.preventDefault()
					toggleRight()
					return
				case "Escape":
					if (ui.threadId) setUi({ threadId: null })
					else if (ui.selectedId) setUi({ selectedId: null })
					else if (ui.authorFilter || ui.filter !== "all")
						setUi({ authorFilter: null, filter: "all" })
					return
			}
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [])
}
